"use client";

/**
 * Renderizza gli allegati media V13 di un evento nella pagina pubblica di
 * dettaglio, raggruppati per tipo:
 *   - VIDEO_URL     → iframe YouTube/embed o <video>
 *   - VIDEO_FILE    → <video controls> con resolveMediaUrl
 *   - PHOTO/GALLERY → griglia immagini (con lightbox stile "apri in nuova tab")
 *   - PDF           → link download + preview iframe opzionale
 *   - SLIDE         → idem PDF
 *   - NOTE          → link download con icona
 *   - PRESS_URL     → link esterno a articolo di giornale
 *   - PRESS_FILE    → link download del PDF/scansione del ritaglio stampa
 *
 * Ogni allegato ha `id="media-{id}"` per ancoraggio da altri blocchi.
 */

import type { EventoMediaItemDto } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/media-url";
import { toYouTubeEmbed } from "@/lib/youtube";
import { MEDIA_TYPE_META } from "@/lib/evento-media";
import { ImageGalleryBlock } from "@/components/GalleryBlocks";

function SectionHeader({ icon, title, count }: { icon: string; title: string; count?: number }) {
  // A11y: text-neutral-400 → text-neutral-500 (WCAG AA su bg-white)
  // A11y: h3 → h2 — questo e' il top-level del media block sotto h1 della pagina
  return (
    <h2 className="flex items-center gap-2 text-xs font-bold text-neutral-500 uppercase tracking-[0.15em] mb-3">
      <span className="text-base" aria-hidden>{icon}</span>
      <span>{title}</span>
      {count != null && count > 1 && <span className="text-neutral-500">({count})</span>}
    </h2>
  );
}

function VideoItem({ item }: { item: EventoMediaItemDto }) {
  // URL esterno (YouTube/Vimeo/URL diretto)
  if (item.mediaType === "VIDEO_URL" && item.externalUrl) {
    const embed = toYouTubeEmbed(item.externalUrl);
    if (embed) {
      return (
        <div className="rounded-xl overflow-hidden shadow-sm border border-neutral-200 bg-black">
          <div className="aspect-video">
            <iframe
              src={embed}
              title={item.title || "Video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        </div>
      );
    }
    // URL video diretto
    return (
      <div className="rounded-xl overflow-hidden shadow-sm border border-neutral-200 bg-black">
        <video src={item.externalUrl} controls className="w-full h-auto" />
      </div>
    );
  }
  // File video caricato
  const url = resolveMediaUrl(item.media);
  if (!url) return null;
  return (
    <div className="rounded-xl overflow-hidden shadow-sm border border-neutral-200 bg-black">
      <video src={url} controls className="w-full h-auto" />
    </div>
  );
}

// BUG-FIX gallery-lightbox-mediaItems: ImageItem rimosso — la galleria
// foto/gallery e' ora renderizzata da ImageGalleryBlock (vedi sotto)
// che gestisce in-page la lightbox con frecce prev/next.

function DocumentItem({ item, accentClass = "text-red-500" }: { item: EventoMediaItemDto; accentClass?: string }) {
  const url = resolveMediaUrl(item.media);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-3 border border-neutral-200 rounded-lg px-4 py-3 text-sm hover:bg-neutral-50 hover:border-neutral-300 transition-colors w-full"
    >
      <span className={`text-2xl ${accentClass}`} aria-hidden>
        {MEDIA_TYPE_META[item.mediaType].icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-medium text-neutral-800 truncate">
          {item.title || item.media?.filename || MEDIA_TYPE_META[item.mediaType].label}
        </span>
        {item.caption && <span className="block text-xs text-neutral-500 mt-0.5 line-clamp-2">{item.caption}</span>}
      </span>
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-neutral-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </a>
  );
}

/**
 * Riga per un articolo di rassegna stampa:
 *  - PRESS_URL  → usa externalUrl e mostra l'hostname come sottotitolo
 *  - PRESS_FILE → usa il file caricato (PDF/scansione)
 */
function PressItem({ item }: { item: EventoMediaItemDto }) {
  const fileUrl = resolveMediaUrl(item.media);
  const href = item.mediaType === "PRESS_URL" ? item.externalUrl : fileUrl;
  if (!href) return null;
  let hostname: string | null = null;
  if (item.mediaType === "PRESS_URL" && item.externalUrl) {
    try { hostname = new URL(item.externalUrl).hostname.replace(/^www\./, ""); } catch { /* ignore */ }
  }
  const meta = MEDIA_TYPE_META[item.mediaType];
  return (
    // BUG-FIX press-review-mobile-truncate:
    //   Pre-fix la box era {@code inline-flex items-center} (verticalmente
    //   centrato) e i due {@code <span>} interni avevano {@code truncate}
    //   (1 riga + ellipsis). Sui breakpoint stretti del mobile/2-col i
    //   titoli giornali lunghi venivano tagliati a "Terra Santa: Reggio
    //   Emilia, stasera i…" perdendo informazione utile.
    //   Fix: {@code items-start} (allineamento top, l'icona resta in
    //   linea con la prima riga del titolo) e {@code line-clamp-2} sia
    //   sul titolo che sul sottotitolo — fino a 2 righe per ognuno,
    //   ellipsis solo se davvero serve.
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-start gap-3 border border-emerald-200 bg-emerald-50/30 rounded-lg px-4 py-3 text-sm hover:bg-emerald-50 hover:border-emerald-300 transition-colors w-full"
    >
      <span className="text-2xl text-emerald-600 leading-none mt-0.5" aria-hidden>{meta.icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block font-medium text-neutral-800 line-clamp-2 break-words">
          {item.title || item.media?.filename || meta.label}
        </span>
        <span className="block text-xs text-emerald-700/80 mt-0.5 line-clamp-2 break-words">
          {item.mediaType === "PRESS_URL" ? (hostname ?? "Articolo online") : "Ritaglio stampa (PDF)"}
          {item.caption && <span className="text-neutral-500"> — {item.caption}</span>}
        </span>
      </span>
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        {item.mediaType === "PRESS_URL" ? (
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        )}
      </svg>
    </a>
  );
}

export function EventoMediaSections({ items }: { items: EventoMediaItemDto[] | null | undefined }) {
  if (!items?.length) return null;

  // Ordino per position (il BE già ordina, ma mettiamoci al sicuro)
  const sorted = [...items].sort((a, b) => a.position - b.position);

  const videos = sorted.filter((i) => i.mediaType === "VIDEO_URL" || i.mediaType === "VIDEO_FILE");
  const images = sorted.filter((i) => i.mediaType === "PHOTO" || i.mediaType === "GALLERY");
  const pdfs = sorted.filter((i) => i.mediaType === "PDF");
  const slides = sorted.filter((i) => i.mediaType === "SLIDE");
  const notes = sorted.filter((i) => i.mediaType === "NOTE");
  const press = sorted.filter((i) => i.mediaType === "PRESS_URL" || i.mediaType === "PRESS_FILE");

  return (
    <div className="space-y-8 mb-8">
      {videos.length > 0 && (
        <section id="media-videos">
          <SectionHeader icon="🎬" title="Video" count={videos.length} />
          <div className="space-y-4">
            {videos.map((v) => (
              <figure key={v.id} id={`media-${v.id}`} className="scroll-mt-24">
                <VideoItem item={v} />
                {(v.title || v.caption) && (
                  <figcaption className="mt-2 text-sm text-neutral-600">
                    {v.title && <span className="font-medium text-neutral-800">{v.title}</span>}
                    {v.title && v.caption && <span> — </span>}
                    {v.caption && <span>{v.caption}</span>}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </section>
      )}

      {images.length > 0 && (
        // BUG-FIX gallery-lightbox-mediaItems:
        //   Pre-fix renderizzavamo una grid di {@code ImageItem}, ciascuno
        //   avvolto in <a href={url} target="_blank"> → cliccare apriva
        //   l'immagine raw del backend (cms.ccpascal.cloud/api/media/{id})
        //   in una nuova tab, senza alcuna navigazione fra le foto della
        //   galleria. Ora usiamo ImageGalleryBlock (stesso componente
        //   ri-utilizzato in /eventi/[slug]/page.tsx per evento.gallery)
        //   che apre una lightbox in-page con frecce prev/next, tastiera
        //   (← → Esc) e contatore "n / total".
        <section id="media-foto" className="scroll-mt-24">
          <SectionHeader icon="🖼" title={images.some((i) => i.mediaType === "GALLERY") ? "Galleria" : "Foto"} count={images.length} />
          <ImageGalleryBlock
            // flatMap invece di map+filter: ritornando [] per gli item
            // senza url evitiamo il type-predicate (TS si confonde tra
            // {caption?: string} e {caption: string | undefined}).
            items={images.flatMap((img) => {
              const url = resolveMediaUrl(img.media);
              if (!url) return [];
              const item: { url: string; alt: string; caption?: string } = {
                url,
                alt: img.title || img.caption || img.media?.filename || "",
              };
              if (img.caption) item.caption = img.caption;
              return [item];
            })}
            layout="grid"
            columns={3}
          />
        </section>
      )}

      {(pdfs.length > 0 || slides.length > 0 || notes.length > 0) && (
        <section id="media-materiali">
          <SectionHeader icon="📚" title="Materiali" count={pdfs.length + slides.length + notes.length} />
          <div className="grid sm:grid-cols-2 gap-3">
            {pdfs.map((it) => (
              <div key={it.id} id={`media-${it.id}`} className="scroll-mt-24"><DocumentItem item={it} accentClass="text-rose-500" /></div>
            ))}
            {slides.map((it) => (
              <div key={it.id} id={`media-${it.id}`} className="scroll-mt-24"><DocumentItem item={it} accentClass="text-violet-500" /></div>
            ))}
            {notes.map((it) => (
              <div key={it.id} id={`media-${it.id}`} className="scroll-mt-24"><DocumentItem item={it} accentClass="text-sky-500" /></div>
            ))}
          </div>
        </section>
      )}

      {press.length > 0 && (
        <section id="media-stampa">
          <SectionHeader icon="📰" title="Rassegna stampa" count={press.length} />
          <div className="grid sm:grid-cols-2 gap-3">
            {press.map((it) => (
              <div key={it.id} id={`media-${it.id}`} className="scroll-mt-24"><PressItem item={it} /></div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

